import { API_BASE_URL } from "../../../config";
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CreateTankDialog, MoveTankDialog, ReturnTankDialog, TankHistoryDialog, STATUS_LABELS, isLocked } from './TankActions';

// A small util to render a map preview via Google Maps iframe
const MapPreview = ({ latitude, longitude, label }) => {
    if (!latitude || !longitude) return <div>ไม่มีพิกัด</div>;
    const lat = String(latitude);
    const lng = String(longitude);
    const q = encodeURIComponent(`${lat},${lng} (${label || ''})`);
    const mapSrc = `https://www.google.com/maps?q=${q}&z=16&output=embed`;
    return (
        <div style={{ width: '100%', height: 250, border: '1px solid #ccc', borderRadius: 8, overflow: 'hidden' }}>
            <iframe title={`map-${lat}-${lng}`} width="100%" height="100%" frameBorder="0" src={mapSrc} />
        </div>
    );
};

const CUSTOMER_ENDPOINT = { REGULAR: 'regular', WALK_IN: 'walkin', AGENT: 'agent', POTENTIAL: 'potential' };
const TYPE_LABELS = { REGULAR: 'ลูกค้าประจำ', WALK_IN: 'ลูกค้าจร', AGENT: 'ตัวแทน', POTENTIAL: 'ลูกค้าที่คาดหวัง' };

const CustomerDetailPage = () => {
    const navigate = useNavigate();
    const { id, type } = useParams();
    const { state } = useLocation();
    const passedCustomer = state?.customerData || null;

    const [customer, setCustomer] = useState(passedCustomer);
    const [tanks, setTanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tankLoading, setTankLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadFailed, setLoadFailed] = useState(false);
    const [showSystem, setShowSystem] = useState(false);
    const [tankError, setTankError] = useState('');
    const [tankReload, setTankReload] = useState(0);
    const [showCreate, setShowCreate] = useState(false);
    const [movingTank, setMovingTank] = useState(null);
    const [returningTank, setReturningTank] = useState(null);
    const [historyTank, setHistoryTank] = useState(null);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            handleLogout();
            return null;
        }
        return JSON.parse(user)?.token;
    }, [handleLogout]);

    // If we didn't receive full customer via state, try to minimally rebuild it from route params
    useEffect(() => {
        if (!customer) {
            setCustomer({ id, type: (type || 'REGULAR').toUpperCase() });
        }
    }, [customer, id, type]);

    // Opened straight from a URL (no router state) → only the code + type are known; fetch the
    // customer ourselves so the page never depends on state from the list page and never falls
    // back to showing the system code.
    useEffect(() => {
        if (!customer || customer.raw) return;
        const user = localStorage.getItem('user');
        const token = user ? JSON.parse(user)?.token : null;
        if (!token) return;
        // Links from pages that only know a customer code default to REGULAR; if that code isn't a
        // regular customer, look it up under the other customer types (a real lookup by code).
        const first = (customer.type || '').toUpperCase();
        const order = [first, ...Object.keys(CUSTOMER_ENDPOINT).filter((t) => t !== first)].filter((t) => CUSTOMER_ENDPOINT[t]);
        let cancelled = false;
        (async () => {
            try {
                for (const t of order) {
                    const res = await fetch(`${API_BASE_URL}/customer/api/customers/${CUSTOMER_ENDPOINT[t]}/${encodeURIComponent(customer.id)}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) continue;
                    const c = await res.json();
                    if (cancelled) return;
                    setCustomer((prev) => ({
                        ...prev,
                        type: t,
                        storeName: prev?.storeName || c?.storeName || c?.customerName || c?.walkInCustomerName || c?.agentName || c?.potentialCustomerName || '',
                        latitude: prev?.latitude ?? c?.latitude,
                        longitude: prev?.longitude ?? c?.longitude,
                        customerAvailable: prev?.customerAvailable ?? (c?.operating === undefined ? undefined : !!c.operating),
                        raw: c,
                    }));
                    return;
                }
                if (!cancelled) setLoadFailed(true);
            } catch (e) {
                console.error('Fetch customer error', e);
                if (!cancelled) setLoadFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, [customer, id, type]);

    // Fetch tanks for REGULAR customers
    useEffect(() => {
        const fetchTanks = async () => {
            if (!customer) { setLoading(false); return; }
            setLoading(false); // customer info considered ready

            if ((customer.type || '').toUpperCase() !== 'REGULAR') return; // Only regular have tanks

            const token = checkToken();
            if (!token) return;

            try {
                setTankLoading(true);
                setTankError('');
                const base = `${API_BASE_URL}/customer/api/customers`;
                const url = `${base}/regular/${customer.id}/tanks`;
                const resp = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (resp.status === 403) { handleLogout(); return; }
                if (!resp.ok) {
                    const text = await resp.text();
                    throw new Error(text || 'Failed to fetch tanks');
                }
                const data = await resp.json();
                setTanks(Array.isArray(data) ? data : (data?.data || []));
            } catch (e) {
                console.error('Fetch tanks error', e);
                setTankError('ไม่สามารถดึงข้อมูลถังได้');
            } finally {
                setTankLoading(false);
            }
        };
        fetchTanks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer?.id, customer?.type, checkToken, handleLogout, tankReload]);

    const afterTankAction = () => {
        setShowCreate(false);
        setMovingTank(null);
        setReturningTank(null);
        setTankReload((n) => n + 1);
    };

    return (
        <div className="btc-container">
            <h1>{customer?.storeName || (loadFailed ? 'ไม่ทราบชื่อ' : 'รายละเอียดร้าน')}</h1>
            <div style={{ color: '#6B6B6B', marginBottom: 4 }}>{TYPE_LABELS[(customer?.type || '').toUpperCase()] || customer?.type || ''}</div>
            <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/customerData'))} className="backButton">ย้อนกลับ</button>
            <br />
            <br />

            {error && <p className="error">{error}</p>}
            {loading ? (
                <p>กำลังโหลด...</p>
            ) : (
                <>
                    {/* Customer basic info */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                            <h3>ข้อมูลร้าน</h3>
                            <div><b>ประเภท:</b> {(customer?.type || '').toString()}</div>
                            <div><b>ชื่อร้าน:</b> {customer?.storeName || 'ไม่ทราบชื่อ'}</div>
                            {customer?.raw?.phone && (<div><b>เบอร์โทร:</b> {customer.raw.phone}</div>)}
                            {customer?.raw?.business && (<div><b>ประเภทธุรกิจ:</b> {customer.raw.business}</div>)}
                            {customer?.raw?.customerClass && (<div><b>เกรดลูกค้า:</b> {customer.raw.customerClass}</div>)}
                            {typeof customer?.customerAvailable === 'boolean' && (
                                <div><b>แสดงให้คนขาย:</b> {customer.customerAvailable ? 'แสดง' : 'ไม่แสดง'}</div>
                            )}
                            <div style={{ marginTop: 8 }}>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setShowSystem((o) => !o)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowSystem((o) => !o); } }}
                                    style={{ cursor: 'pointer', color: '#6B6B6B', borderBottom: '1px dotted #6B6B6B' }}
                                >
                                    ข้อมูลระบบ {showSystem ? '▲' : '▼'}
                                </span>
                                {showSystem && <div style={{ color: '#6B6B6B', fontSize: '0.85rem' }}>รหัสลูกค้า: {customer?.id}</div>}
                            </div>
                            {(customer?.latitude && customer?.longitude) ? (
                                <>
                                    <div><b>ละติจูด:</b> {customer.latitude}</div>
                                    <div><b>ลองจิจูด:</b> {customer.longitude}</div>
                                </>
                            ) : (
                                <div>ไม่มีพิกัดของร้าน</div>
                            )}
                        </div>
                        <div>
                            <h3>แผนที่ตำแหน่งร้าน</h3>
                            <MapPreview latitude={customer?.latitude} longitude={customer?.longitude} label={customer?.storeName} />
                        </div>
                    </div>

                    {/* Tanks for regular customers */}
                    <div style={{ marginTop: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <h3 style={{ margin: 0 }}>ถังน้ำแข็ง</h3>
                            {(customer?.type || '').toUpperCase() === 'REGULAR' && (
                                <button className="btc-btn btn-sm" style={{ backgroundColor: '#E60012' }} onClick={() => setShowCreate(true)}>＋ เพิ่มถังให้ร้านนี้</button>
                            )}
                        </div>
                        {(customer?.type || '').toUpperCase() !== 'REGULAR' && (
                            <div>มีเฉพาะร้านแบบ REGULAR เท่านั้น</div>
                        )}
                        {(customer?.type || '').toUpperCase() === 'REGULAR' && (
                            <>
                                {tankLoading && <div>กำลังดึงข้อมูลถัง...</div>}
                                {tankError && <p className="error">{tankError}</p>}
                                {!tankLoading && !tankError && (
                                    <>
                                        {tanks.length === 0 ? (
                                            <div>ไม่มีถังที่บันทึกไว้</div>
                                        ) : (
                                            <table className="btc-table" style={{ marginTop: 8 }}>
                                                <thead>
                                                    <tr>
                                                        <th>รหัสถัง</th>
                                                        <th>สถานะ</th>
                                                        <th>Label</th>
                                                        <th>Size</th>
                                                        <th>Lat</th>
                                                        <th>Lng</th>
                                                        <th>Date Placed</th>
                                                        <th>แผนที่</th>
                                                        <th>จัดการ</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tanks.map((tank) => (
                                                        <tr key={tank.tankId || tank.id}>
                                                            <td>{tank.tankId || '-'}</td>
                                                            <td>{STATUS_LABELS[tank.status] || tank.status || '-'}</td>
                                                            <td>{tank.label || '-'}</td>
                                                            <td>{tank.size || '-'}</td>
                                                            <td>{tank.latitude || '-'}</td>
                                                            <td>{tank.longitude || '-'}</td>
                                                            <td>{tank.datePlaced || '-'}</td>
                                                            <td style={{ minWidth: 260 }}>
                                                                <MapPreview latitude={tank.latitude} longitude={tank.longitude} label={tank.label} />
                                                            </td>
                                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                                {!isLocked(tank) && (
                                                                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#E60012', marginRight: 6 }} onClick={() => setMovingTank(tank)}>ย้าย</button>
                                                                )}
                                                                {!isLocked(tank) && (
                                                                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#6B6B6B', marginRight: 6 }} onClick={() => setReturningTank(tank)}>ถอนคืนโรงงาน</button>
                                                                )}
                                                                <button className="btc-btn btn-sm" style={{ backgroundColor: '#6B6B6B' }} onClick={() => setHistoryTank(tank)}>ประวัติ</button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}

            {showCreate && (
                <CreateTankDialog
                    presetCustomer={{ code: customer?.id, name: customer?.storeName }}
                    onClose={() => setShowCreate(false)}
                    onDone={afterTankAction}
                />
            )}
            {movingTank && <MoveTankDialog tank={movingTank} onClose={() => setMovingTank(null)} onDone={afterTankAction} />}
            {returningTank && <ReturnTankDialog tank={returningTank} onClose={() => setReturningTank(null)} onDone={afterTankAction} />}
            {historyTank && <TankHistoryDialog tank={historyTank} onClose={() => setHistoryTank(null)} />}
        </div>
    );
};

// Keyed by the route so moving from one customer's page to another (via a name link) starts clean.
const CustomerDetail = () => {
    const { id, type } = useParams();
    return <CustomerDetailPage key={`${type}/${id}`} />;
};

export default CustomerDetail;
